/**
 * Ficha da escola — página inteira, com endereço próprio.
 *
 * Era um modal. Modal é certo para uma decisão curta ("confirma?"), e errado
 * para consulta: não cabe, não tem endereço para mandar a alguém, não volta ao
 * mesmo lugar depois de recarregar, e obriga a fechar para ver a próxima. Esta
 * ficha é o material de uma ligação comercial — precisa caber na tela e ser
 * linkável.
 */

import { useQuery } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from 'react-router-dom'

import { api, type Item } from '../api/cliente'
import { Composicao, Evolucao, Posicao, SERIES, SERIE_4 } from '../componentes/graficos'
import { Aviso, Botao, Carregando, Cartao, Etiqueta, Vazio, formatarData } from '../componentes/ui'
import { ROTULOS_FUNIL, type SituacaoFunil } from '@egba-do/shared'

const TOM: Record<SituacaoFunil, 'neutro' | 'marca' | 'sucesso' | 'atencao'> = {
  cliente: 'sucesso',
  em_negociacao: 'marca',
  lead: 'atencao',
  nao_trabalhada: 'neutro',
}

const brl = (c: number): string =>
  (c / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 })
const num = (n: number): string => n.toLocaleString('pt-BR')

interface Detalhe {
  id: string
  coEntidade: number
  anoCenso: number
  noEntidade: string
  noMunicipio: string | null
  dsEndereco: string | null
  nuEndereco: string | null
  coCep: string | null
  nuDdd: string | null
  nuTelefone: string | null
  razaoSocial: string | null
  nomeFantasia: string | null
  cnpjEscola: string | null
  cnpjMantenedora: string | null
  porte: string | null
  capitalSocial: string | null
  situacaoCadastral: string | null
  dataInicioAtividade: string | null
  cnaePrincipal: string | null
  emailReceita: string | null
  telefoneReceita: string | null
  enriquecidoEm: string | null
  origemVinculo: 'declarado' | 'nome' | 'nome_cep' | null
  confiancaMatch: string | null
  cnpjVinculado: string | null
  motivoSemVinculo: 'sem_cnpj_declarado' | 'nao_encontrado' | 'ambiguo' | null
  ofereceMedio: boolean
  ofereceTecnico: boolean
  qtMatBasica: number
  qtMatMedio: number
  medio3: number
  medioTec3: number
  medioNormal3: number
  tecConc: number
  tecSubs: number
  ejaMedio: number
  situacao: SituacaoFunil
  potencial: {
    matriculasElegiveis: number
    concluintesEstimados: number
    cmColunaEstimado: number
    valorEstimadoCentavos: number
  }
  metrica: { taxaConclusao: number; fracaoConclusaoTecnico: number; colunas: number; alturaLinhaCm: number }
  valorCmColunaCentavos: number
  posicao: { acima: number; total: number }
  historico: Array<{ ano: number; matriculas: number; medio: number; medio3: number }>
}

function Dado({ rotulo, valor, largo }: { rotulo: string; valor: string | null | undefined; largo?: boolean }) {
  return (
    <div className={largo ? 'sm:col-span-2' : ''}>
      <dt className="text-xs text-slate-500">{rotulo}</dt>
      <dd className="mt-0.5 text-sm font-medium break-words text-slate-900">{valor || '—'}</dd>
    </div>
  )
}

function cnpjFormatado(c: string | null): string | null {
  if (!c || c.length !== 14) return c
  return `${c.slice(0, 2)}.${c.slice(2, 5)}.${c.slice(5, 8)}/${c.slice(8, 12)}-${c.slice(12)}`
}

export function EscolaMercado() {
  const { id = '' } = useParams()
  const navegar = useNavigate()

  const { data, isLoading, error } = useQuery({
    queryKey: ['mercado-escola', id],
    queryFn: () => api.get<Item<Detalhe>>(`/api/v1/mercado/escolas/${id}`),
    retry: false,
  })

  if (isLoading) return <Carregando />
  if (error || !data) {
    return (
      <Vazio
        titulo="Escola não encontrada"
        descricao="O endereço pode estar desatualizado, ou a escola saiu do Censo importado."
        acao={<Botao onClick={() => navegar('/mercado')}>Voltar ao mercado</Botao>}
      />
    )
  }

  const e = data.dados
  const p = e.potencial
  const telefone =
    e.nuDdd && e.nuTelefone ? `(${e.nuDdd}) ${e.nuTelefone}` : (e.telefoneReceita ?? null)

  const etapas = [
    { rotulo: '3ª série — propedêutico', valor: e.medio3 },
    { rotulo: '3ª série — integrado ao técnico', valor: e.medioTec3 },
    { rotulo: '3ª série — normal/magistério', valor: e.medioNormal3 },
    { rotulo: 'EJA de nível médio', valor: e.ejaMedio },
    { rotulo: 'Técnico concomitante', valor: e.tecConc },
    { rotulo: 'Técnico subsequente', valor: e.tecSubs },
  ]

  const anos = e.historico.map((h) => h.ano)
  const temSerie = e.historico.length > 1

  return (
    <div className="space-y-5">
      {/* ------------------------------------------------------------ topo */}
      <div>
        <Link to="/mercado" className="text-sm text-slate-500 transition hover:text-slate-900">
          ← Mercado de escolas
        </Link>
        <div className="mt-2 flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-[26px] leading-tight font-semibold tracking-tight text-slate-900">
              {e.noEntidade}
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              {e.noMunicipio} · código INEP {e.coEntidade} · Censo {e.anoCenso}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Etiqueta tom={TOM[e.situacao]}>{ROTULOS_FUNIL[e.situacao].nome}</Etiqueta>
            {e.ofereceMedio && <Etiqueta tom="marca">ensino médio</Etiqueta>}
            {e.ofereceTecnico && <Etiqueta tom="marca">técnico</Etiqueta>}
            {e.situacaoCadastral && <Etiqueta tom="neutro">{e.situacaoCadastral}</Etiqueta>}
          </div>
        </div>
      </div>

      {/* ------------------------------------------------------- potencial */}
      <div className="grid gap-4 lg:grid-cols-[1fr_1fr]">
        <Cartao titulo="Potencial anual" descricao="Estimativa para priorizar o contato — não é orçamento.">
          <p className="text-4xl font-semibold tracking-tight text-marca-700">
            {brl(p.valorEstimadoCentavos)}
          </p>

          {/* A conta aberta. Um número sozinho não se discute ao telefone; a
              conta aberta, sim — e é o que a escola vai perguntar. */}
          <ol className="mt-4 space-y-2 text-sm">
            {[
              [`${num(p.matriculasElegiveis)} matrículas`, 'nas etapas que concluem, do Censo'],
              [
                `${num(p.concluintesEstimados)} concluintes`,
                `${Math.round(e.metrica.taxaConclusao * 100)}% de aprovação no médio, ${Math.round(e.metrica.fracaoConclusaoTecnico * 100)}% do técnico`,
              ],
              [
                `${num(p.cmColunaEstimado)} cm-coluna`,
                `${e.metrica.colunas} colunas, linha de ${e.metrica.alturaLinhaCm} cm`,
              ],
              [brl(p.valorEstimadoCentavos), `a ${brl(e.valorCmColunaCentavos)} por cm-coluna`],
            ].map(([valor, nota], i) => (
              <li key={valor} className="flex gap-3">
                <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-marca-100 text-[11px] font-semibold text-marca-700">
                  {i + 1}
                </span>
                <span>
                  <strong className="text-slate-900">{valor}</strong>
                  <span className="block text-xs text-slate-500">{nota}</span>
                </span>
              </li>
            ))}
          </ol>
        </Cartao>

        <div className="space-y-4">
          <Cartao titulo="Posição no mercado">
            <Posicao acima={e.posicao.acima} total={e.posicao.total} />
          </Cartao>
          <Cartao titulo="De onde vêm os concluintes">
            <Composicao
              partes={[
                { rotulo: 'Ensino médio', valor: e.medio3 + e.medioNormal3, cor: SERIES[0] },
                { rotulo: 'Integrado ao técnico', valor: e.medioTec3, cor: SERIES[2] },
                { rotulo: 'Técnico conc./subs.', valor: e.tecConc + e.tecSubs, cor: SERIES[1] },
                { rotulo: 'EJA médio', valor: e.ejaMedio, cor: SERIE_4 },
              ]}
            />
          </Cartao>
        </div>
      </div>

      {/* --------------------------------------------------------- matrículas */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Cartao titulo="Matrículas por etapa" descricao={`Censo Escolar ${e.anoCenso} — dado publicado.`}>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-3">
            <Dado rotulo="Total, todas as etapas" valor={num(e.qtMatBasica)} />
            <Dado rotulo="Ensino médio (todas as séries)" valor={num(e.qtMatMedio)} />
          </dl>
          <div className="mt-4 space-y-1.5 border-t border-[color:var(--color-borda-suave)] pt-4">
            {etapas.map((et) => (
              <div key={et.rotulo} className="flex items-center gap-3">
                <span className="flex-1 text-[13px] text-slate-600">{et.rotulo}</span>
                <span className="relative h-5 w-40 overflow-hidden rounded bg-slate-50">
                  <span
                    className="absolute inset-y-0 left-0 rounded-r"
                    style={{
                      width: `${Math.max(et.valor > 0 ? 3 : 0, (et.valor / Math.max(1, ...etapas.map((x) => x.valor))) * 100)}%`,
                      background: SERIES[0],
                    }}
                  />
                </span>
                <span className="w-14 text-right text-sm font-medium text-slate-800 tabular-nums">
                  {num(et.valor)}
                </span>
              </div>
            ))}
          </div>
        </Cartao>

        <Cartao
          titulo="Evolução"
          descricao={
            temSerie
              ? `Censo ${anos[0]} a ${anos[anos.length - 1]}.`
              : 'Só um ano de Censo importado até aqui.'
          }
        >
          {temSerie ? (
            <>
              <Evolucao
                anos={anos}
                series={[
                  { nome: 'Total', valores: e.historico.map((h) => h.matriculas) },
                  { nome: 'Médio', valores: e.historico.map((h) => h.medio) },
                  { nome: '3ª série', valores: e.historico.map((h) => h.medio3) },
                ]}
              />
              {(() => {
                const a = e.historico[0]!.medio3
                const b = e.historico[e.historico.length - 1]!.medio3
                if (a === 0) return null
                const var3 = ((b - a) / a) * 100
                return (
                  <p className="mt-3 text-sm text-slate-600">
                    A 3ª série {var3 >= 0 ? 'cresceu' : 'caiu'}{' '}
                    <strong className={var3 >= 0 ? 'text-emerald-700' : 'text-red-700'}>
                      {Math.abs(Math.round(var3))}%
                    </strong>{' '}
                    entre {anos[0]} e {anos[anos.length - 1]} — de {num(a)} para {num(b)} matrículas.
                  </p>
                )
              })()}
            </>
          ) : (
            <p className="text-sm text-slate-500">
              Importe outro ano para comparar:{' '}
              <code className="rounded bg-slate-100 px-1 py-0.5 text-xs">
                mercado:importar -- --ano 2023 --uf BA --privadas
              </code>
            </p>
          )}
        </Cartao>
      </div>

      {/* ------------------------------------------------------- cadastros */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Cartao titulo="Contato e endereço" descricao="Como o INEP e a Receita registram esta escola.">
          <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
            <Dado rotulo="Endereço" valor={[e.dsEndereco, e.nuEndereco].filter(Boolean).join(', ')} largo />
            <Dado rotulo="CEP" valor={e.coCep} />
            <Dado rotulo="Município" valor={e.noMunicipio} />
            <Dado rotulo="Telefone" valor={telefone} />
            <Dado rotulo="E-mail (Receita)" valor={e.emailReceita} />
          </dl>
          {telefone || e.emailReceita ? (
            <div className="mt-4 flex flex-wrap gap-2">
              {telefone && (
                <a
                  href={`tel:${telefone.replace(/\D/g, '')}`}
                  className="rounded-lg border border-[color:var(--color-borda)] px-3 py-1.5 text-sm text-slate-700 transition hover:border-marca-400 hover:text-marca-700"
                >
                  Ligar
                </a>
              )}
              {e.emailReceita && (
                <a
                  href={`mailto:${e.emailReceita}`}
                  className="rounded-lg border border-[color:var(--color-borda)] px-3 py-1.5 text-sm text-slate-700 transition hover:border-marca-400 hover:text-marca-700"
                >
                  Enviar e-mail
                </a>
              )}
            </div>
          ) : (
            <Aviso tom="atencao">Sem telefone nem e-mail no Censo e na Receita.</Aviso>
          )}
        </Cartao>

        <Cartao titulo="Pessoa jurídica" descricao="Base de CNPJ da Receita Federal.">
          {/* De onde veio o vínculo, sempre visível.
              Sem isso, um palpite chega à tela com a mesma cara de um cadastro
              confirmado — e alguém liga para a empresa errada com a informação
              errada. */}
          {e.razaoSocial && e.origemVinculo === 'declarado' && (
            <Aviso tom="sucesso" titulo="Vínculo confirmado">
              CNPJ informado pela própria escola ao INEP. Não é palpite.
            </Aviso>
          )}
          {e.razaoSocial && e.origemVinculo === 'nome_cep' && (
            <Aviso tom="atencao" titulo="Encontrado por nome e CEP — confira antes de usar">
              A escola não declarou CNPJ ao INEP. Esta empresa foi localizada por{' '}
              <strong>semelhança de nome no mesmo CEP</strong> ({Math.round(Number(e.confiancaMatch ?? 0) * 100)}
              % de semelhança), sem outro candidato próximo. Duas evidências independentes
              apontam para ela, mas é hipótese.
            </Aviso>
          )}
          {e.razaoSocial && e.origemVinculo === 'nome' && (
            <Aviso tom="atencao" titulo="Encontrado só por nome — confira antes de usar">
              A escola não declarou CNPJ ao INEP. Esta empresa foi localizada por semelhança de
              nome no mesmo município ({Math.round(Number(e.confiancaMatch ?? 0) * 100)}%), sem
              confirmação de endereço. É hipótese.
            </Aviso>
          )}

          {e.enriquecidoEm && !e.razaoSocial ? (
            <Aviso tom="atencao">
              {e.motivoSemVinculo === 'ambiguo'
                ? 'Encontrei mais de uma empresa igualmente parecida no mesmo município. Vincular a uma delas seria acertar por sorte, então não vinculei — o CNPJ precisa ser confirmado à mão.'
                : e.cnpjEscola || e.cnpjMantenedora
                  ? 'O CNPJ declarado ao INEP não foi encontrado na base da Receita — costuma ser cadastro desatualizado no Censo.'
                  : 'Esta escola não declarou CNPJ ao INEP, e a busca por nome não encontrou nenhuma empresa parecida o bastante no município.'}
            </Aviso>
          ) : (
            <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
              <Dado rotulo="Razão social" valor={e.razaoSocial} largo />
              <Dado rotulo="Nome fantasia" valor={e.nomeFantasia} largo />
              <Dado
                rotulo={e.origemVinculo === 'declarado' ? 'CNPJ declarado ao INEP' : 'CNPJ encontrado'}
                valor={cnpjFormatado(e.cnpjVinculado ?? e.cnpjEscola)}
              />
              <Dado rotulo="CNPJ da mantenedora" valor={cnpjFormatado(e.cnpjMantenedora)} />
              <Dado rotulo="Porte" valor={e.porte} />
              <Dado
                rotulo="Capital social"
                valor={e.capitalSocial ? brl(Number(e.capitalSocial) * 100) : null}
              />
              <Dado rotulo="Situação cadastral" valor={e.situacaoCadastral} />
              <Dado
                rotulo="Início de atividade"
                valor={e.dataInicioAtividade ? formatarData(e.dataInicioAtividade) : null}
              />
              <Dado rotulo="CNAE principal" valor={e.cnaePrincipal} largo />
            </dl>
          )}
          <p className="mt-4 text-[11px] leading-snug text-slate-400">
            Porte e capital social são o que existe de público sobre o tamanho da empresa. Nenhum
            dos dois é faturamento, e nenhum entra no cálculo do potencial acima.
          </p>
        </Cartao>
      </div>
    </div>
  )
}
